import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/Navigation';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Plus, Trophy, Calendar, Copy, Check, WhatsappLogo } from '@phosphor-icons/react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeaguesPage = () => {
  const { t } = useTranslation();
  const [leagues, setLeagues] = useState([]);
  const [globalLeaderboard, setGlobalLeaderboard] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [leagueLeaderboard, setLeagueLeaderboard] = useState([]);
  const [period, setPeriod] = useState('weekly');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [createdLeagueCode, setCreatedLeagueCode] = useState('');
  const [createdLeagueName, setCreatedLeagueName] = useState('');
  const [copiedCode, setCopiedCode] = useState(null);
  const [leagueName, setLeagueName] = useState('');
  const [leagueCode, setLeagueCode] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [period]);

  useEffect(() => {
    if (selectedLeague) {
      fetchLeagueLeaderboard();
    }
  }, [selectedLeague, period]);

  const fetchData = async () => {
    try {
      const [leaguesRes, globalRes] = await Promise.all([
        axios.get(`${API}/leagues`),
        axios.get(`${API}/leaderboards/${period}`),
      ]);

      setLeagues(leaguesRes.data);
      setGlobalLeaderboard(globalRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeagueLeaderboard = async () => {
    try {
      const res = await axios.get(`${API}/leaderboards/${period}?league_id=${selectedLeague}`);
      setLeagueLeaderboard(res.data);
    } catch (error) {
      console.error('Failed to fetch league leaderboard:', error);
    }
  };

  const handleCreateLeague = async () => {
    if (!leagueName.trim()) {
      toast.error(t('leagues.enterName'));
      return;
    }

    try {
      const res = await axios.post(`${API}/leagues`, { name: leagueName });
      setCreatedLeagueCode(res.data.code);
      setCreatedLeagueName(res.data.name);
      setLeagueName('');
      setShowCreateModal(false);
      setShowInviteModal(true);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create league');
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success(t('leagues.codeCopied'));
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleJoinLeague = async () => {
    if (!leagueCode.trim()) {
      toast.error(t('leagues.enterCode'));
      return;
    }

    try {
      await axios.post(`${API}/leagues/${leagueCode.toUpperCase().trim()}/join`);
      toast.success(t('leagues.joinedSuccess'));
      setLeagueCode('');
      setShowJoinModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join league');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-text-secondary">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background noise-bg">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-white" data-testid="leagues-title">
            {t('leagues.title')}
          </h1>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowJoinModal(true)}
              data-testid="join-league-button"
              className="px-4 py-2 bg-transparent border border-primary text-primary hover:bg-primary hover:text-black font-bold uppercase tracking-wide rounded-sm transition-all"
            >
              {t('leagues.join')}
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              data-testid="create-league-button"
              className="flex items-center space-x-2 px-4 py-2 bg-primary hover:bg-primary-hover text-black font-bold uppercase tracking-wide rounded-sm transition-all hover:scale-105 glow-effect"
            >
              <Plus size={20} weight="bold" />
              <span>{t('leagues.create')}</span>
            </button>
          </div>
        </div>

        <Tabs defaultValue="global" className="w-full">
          <TabsList className="bg-background-paper border border-border rounded-sm p-1 mb-6">
            <TabsTrigger value="global" data-testid="tab-global" className="data-[state=active]:bg-primary data-[state=active]:text-black">
              {t('leagues.global')}
            </TabsTrigger>
            <TabsTrigger value="private" data-testid="tab-private" className="data-[state=active]:bg-primary data-[state=active]:text-black">
              {t('leagues.private')}
            </TabsTrigger>
          </TabsList>

          {/* Global League */}
          <TabsContent value="global" data-testid="global-leaderboard">
            <div className="bg-background-paper border border-border rounded-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-2xl font-bold text-white">{t('leagues.global')}</h2>
                <div className="flex space-x-2">
                  {['weekly', 'monthly', 'yearly'].map((p) => (
                    <button
                      key={p}
                      onClick={() => setPeriod(p)}
                      data-testid={`period-${p}`}
                      className={`px-4 py-2 rounded-sm text-sm font-medium uppercase tracking-wide transition-all ${
                        period === p
                          ? 'bg-primary text-black'
                          : 'bg-background-subtle text-text-secondary hover:text-primary'
                      }`}
                    >
                      {t(`leagues.${p}`)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                {globalLeaderboard.map((entry, index) => (
                  <div
                    key={entry.user_id}
                    data-testid={`global-rank-${index + 1}`}
                    className="flex items-center justify-between p-4 bg-background-subtle border border-border hover:border-primary/50 rounded-sm transition-all"
                  >
                    <div className="flex items-center space-x-4">
                      <span className={`font-mono font-bold text-2xl w-12 ${
                        index === 0 ? 'text-primary' : index === 1 ? 'text-secondary' : index === 2 ? 'text-accent' : 'text-text-muted'
                      }`}>
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-medium text-white">{entry.display_name || entry.email?.split('@')[0]}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Trophy size={20} className="text-primary" />
                      <span className="font-mono text-2xl font-bold text-primary">{entry.points}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Private Leagues */}
          <TabsContent value="private" data-testid="private-leagues">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Leagues List */}
              <div className="space-y-3">
                <h3 className="font-heading text-xl font-bold text-white mb-4">{t('leagues.myLeagues')}</h3>
                {leagues.length === 0 ? (
                  <div className="bg-background-paper border border-border rounded-sm p-8 text-center">
                    <Users size={48} className="text-text-muted mx-auto mb-3" />
                    <p className="text-text-secondary text-sm">{t('leagues.noLeagues')}</p>
                  </div>
                ) : (
                  leagues.map((league) => (
                    <button
                      key={league.id}
                      onClick={() => setSelectedLeague(league.id)}
                      data-testid={`league-${league.id}`}
                      className={`w-full p-4 rounded-sm border-2 transition-all text-left ${
                        selectedLeague === league.id
                          ? 'border-primary bg-primary/10'
                          : 'border-border bg-background-paper hover:border-primary/50'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-heading text-lg font-bold text-white">{league.name}</p>
                        <Users size={20} className="text-primary" />
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center space-x-1">
                          <span className="text-text-muted font-mono bg-background px-2 py-0.5 rounded">{league.code}</span>
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); copyToClipboard(league.code); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); copyToClipboard(league.code); } }}
                            data-testid={`copy-code-${league.id}`}
                            className="p-1 hover:bg-primary/20 rounded transition-all cursor-pointer"
                          >
                            {copiedCode === league.code ? <Check size={14} className="text-green-400" /> : <Copy size={14} className="text-text-muted hover:text-primary" />}
                          </span>
                        </div>
                        <span className="text-text-muted">{league.members.length} {t('leagues.members')}</span>
                      </div>
                    </button>
                  ))
                )}
              </div>

              {/* League Leaderboard */}
              <div className="lg:col-span-2">
                {selectedLeague ? (
                  <div className="bg-background-paper border border-border rounded-sm p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="font-heading text-2xl font-bold text-white">
                        {leagues.find(l => l.id === selectedLeague)?.name}
                      </h3>
                      <div className="flex space-x-2">
                        {['weekly', 'monthly', 'yearly'].map((p) => (
                          <button
                            key={p}
                            onClick={() => setPeriod(p)}
                            data-testid={`league-period-${p}`}
                            className={`px-3 py-1 rounded-sm text-xs font-medium uppercase tracking-wide transition-all ${
                              period === p
                                ? 'bg-primary text-black'
                                : 'bg-background-subtle text-text-secondary hover:text-primary'
                            }`}
                          >
                            {t(`leagues.${p}`)}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      {leagueLeaderboard.map((entry, index) => (
                        <div
                          key={entry.user_id}
                          data-testid={`league-rank-${index + 1}`}
                          className="flex items-center justify-between p-4 bg-background-subtle border border-border rounded-sm"
                        >
                          <div className="flex items-center space-x-4">
                            <span className={`font-mono font-bold text-xl w-8 ${
                              index === 0 ? 'text-primary' : index === 1 ? 'text-secondary' : index === 2 ? 'text-accent' : 'text-text-muted'
                            }`}>
                              {index + 1}
                            </span>
                            <p className="font-medium text-white">{entry.display_name || entry.email?.split('@')[0]}</p>
                          </div>
                          <span className="font-mono text-xl font-bold text-primary">{entry.points}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="bg-background-paper border border-border rounded-sm p-12 text-center">
                    <Calendar size={64} className="text-text-muted mx-auto mb-4" />
                    <p className="text-text-secondary">{t('leagues.selectLeague')}</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create League Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" data-testid="create-league-modal">
          <div className="bg-background-paper border border-border rounded-sm p-8 max-w-md w-full mx-4">
            <h3 className="font-heading text-2xl font-bold text-white mb-6">{t('leagues.create')}</h3>
            <input
              type="text"
              value={leagueName}
              onChange={(e) => setLeagueName(e.target.value)}
              data-testid="league-name-input"
              placeholder={t('leagues.leagueName')}
              className="w-full px-4 py-3 bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary rounded-sm text-white placeholder:text-text-muted outline-none transition-all mb-4"
            />
            <div className="flex space-x-3">
              <button
                onClick={() => setShowCreateModal(false)}
                data-testid="cancel-create-button"
                className="flex-1 py-3 bg-transparent border border-border text-text-secondary hover:border-primary hover:text-primary rounded-sm transition-all"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreateLeague}
                data-testid="confirm-create-button"
                className="flex-1 py-3 bg-primary hover:bg-primary-hover text-black font-bold uppercase tracking-wide rounded-sm transition-all"
              >
                {t('common.create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Join League Modal */}
      {showJoinModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" data-testid="join-league-modal">
          <div className="bg-background-paper border border-border rounded-sm p-8 max-w-md w-full mx-4">
            <h3 className="font-heading text-2xl font-bold text-white mb-2">{t('leagues.join')}</h3>
            <p className="text-text-muted text-sm mb-6">{t('leagues.joinDescription')}</p>
            <input
              type="text"
              value={leagueCode}
              onChange={(e) => setLeagueCode(e.target.value.toUpperCase())}
              data-testid="league-code-input"
              placeholder="KINGS-XXXX"
              className="w-full px-4 py-3 bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary rounded-sm text-white placeholder:text-text-muted outline-none transition-all mb-4 font-mono text-center text-lg tracking-widest"
            />
            <div className="flex space-x-3">
              <button
                onClick={() => setShowJoinModal(false)}
                data-testid="cancel-join-button"
                className="flex-1 py-3 bg-transparent border border-border text-text-secondary hover:border-primary hover:text-primary rounded-sm transition-all"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleJoinLeague}
                data-testid="confirm-join-button"
                className="flex-1 py-3 bg-primary hover:bg-primary-hover text-black font-bold uppercase tracking-wide rounded-sm transition-all"
              >
                {t('common.join')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Code Modal (shown after league creation) */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" data-testid="invite-code-modal">
          <div className="bg-background-paper border border-border rounded-sm p-8 max-w-md w-full mx-4 text-center">
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
              <Trophy size={32} className="text-primary" />
            </div>
            <h3 className="font-heading text-2xl font-bold text-white mb-2">{t('leagues.leagueCreated')}</h3>
            <p className="text-text-muted text-sm mb-6">{t('leagues.shareCodeDescription')}</p>
            
            <div className="bg-background border-2 border-primary/50 rounded-sm p-4 mb-4">
              <p className="text-text-muted text-xs uppercase tracking-wide mb-1">{t('leagues.inviteCode')}</p>
              <p className="font-mono text-3xl font-bold text-primary tracking-widest" data-testid="created-invite-code">{createdLeagueCode}</p>
            </div>

            <button
              onClick={() => copyToClipboard(createdLeagueCode)}
              data-testid="copy-invite-code-button"
              className="w-full py-3 bg-primary/10 border border-primary text-primary hover:bg-primary hover:text-black font-bold uppercase tracking-wide rounded-sm transition-all flex items-center justify-center space-x-2 mb-3"
            >
              {copiedCode === createdLeagueCode ? (
                <><Check size={20} /><span>{t('leagues.copied')}</span></>
              ) : (
                <><Copy size={20} /><span>{t('leagues.copyCode')}</span></>
              )}
            </button>

            <a
              href={`https://wa.me/?text=${encodeURIComponent(t('leagues.whatsappMessage', { name: createdLeagueName, code: createdLeagueCode }))}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-share-button"
              className="w-full py-3 bg-[#25D366] hover:bg-[#1da851] text-white font-bold uppercase tracking-wide rounded-sm transition-all flex items-center justify-center space-x-2 mb-3"
            >
              <WhatsappLogo size={20} weight="fill" />
              <span>{t('leagues.shareWhatsApp')}</span>
            </a>

            <button
              onClick={() => setShowInviteModal(false)}
              data-testid="close-invite-modal-button"
              className="w-full py-3 bg-primary hover:bg-primary-hover text-black font-bold uppercase tracking-wide rounded-sm transition-all"
            >
              {t('common.done')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaguesPage;
